import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm, type FieldError } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
// Imported per module rather than through the `@/components/ui` barrel: the
// barrel re-exports SetupGuideAffordances, which pulls the authenticated axios
// client into this pre-authentication route (and into its test graph). The
// other auth pages import the same way.
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { LoginBrandPanel } from '@/components/auth/LoginBrandPanel'
import { useSignup, type SignupErrorBody } from '@/hooks/useSignup'
import { PRIVACY_POLICY_URL, TERMS_URL } from '@/lib/legalLinks'

// The window is SERVER-owned and is mirrored here only so the success panel
// can state it. It has exactly one source of truth on the API side, named here
// so a drift is visible at the next read:
//   LINK_VALID_HOURS  <- Devise.reset_password_within
//                        (api/config/initializers/devise.rb)
const LINK_VALID_HOURS = 6

// The marketing site has no /contact page (EP-126: it 404s). The lead form is
// a section on the per-locale home page, so the link has to be the anchor.
const CONTACT_URL_TH = 'https://flowrent.io/#contact'
const CONTACT_URL_EN = 'https://flowrent.io/en#contact'

const INVITATION_CODE_LENGTH = 6

// EP-151: the invitation code mail links to /signup?code=K7M2QP so the
// applicant does not retype the code. Only a value shaped like a code is
// taken. Anything else in the query leaves the field empty, because a field
// that arrives filled with something the applicant never typed and cannot
// submit is worse than an empty one. The server stays the authority on
// whether the code is a real one.
const INVITATION_CODE_SHAPE = /^[A-Z0-9]{6}$/

function invitationCodeFromQuery(raw: string | null): string {
  const value = (raw ?? '').trim().toUpperCase()
  return INVITATION_CODE_SHAPE.test(value) ? value : ''
}

// The names are the wire names: `error.details` from the server is keyed by
// exactly these, so a validation_failed body maps onto the form without a
// translation table. `invitation_code` never appears in details (it has its
// own error code) but shares the namespace.
const FORM_FIELDS = [
  'account_name',
  'owner_first_name',
  'owner_last_name',
  'email',
  'phone',
  'address',
  'invitation_code',
  'terms_accepted',
  'support_onboarding_grant',
] as const

type SignupField = (typeof FORM_FIELDS)[number]

function isSignupField(key: string): key is SignupField {
  return (FORM_FIELDS as readonly string[]).includes(key)
}

// The server's own caps (Api::RegistrationsController NAME_MAX,
// PERSON_NAME_MAX, ADDRESS_MAX). Mirrored so an over-long field is refused
// before the round trip rather than after it; the server stays the authority
// and now answers in the applicant's language either way.
const ACCOUNT_NAME_MAX = 120
const PERSON_NAME_MAX = 60
const ADDRESS_MAX = 500

// Server-side the number is checked with PhonyRails.plausible_number?
// (default country TH), which is stricter than anything worth reimplementing
// here; the server stays the authority. The client rule only has to be no
// LOOSER than that, so it takes two passes: the characters have to be a phone
// number's characters (an optional leading +, then digits and the usual
// separators), and the SIGNIFICANT DIGITS have to be 8..15. Fifteen is the
// E.164 maximum and eight is below any Thai number, so nothing plausible is
// refused. Counting digits rather than characters is the point: the old
// `{9,20}`-character rule accepted "( ) - - - - - - - - -" and rejected a
// generously spaced real number.
const PHONE_ALLOWED_CHARS = /^\+?[\d\s()-]+$/
const PHONE_MIN_DIGITS = 8
const PHONE_MAX_DIGITS = 15

function digitCount(value: string) {
  return value.replace(/\D/g, '').length
}

// The Zod messages below are i18n KEYS, not sentences (owner ruling, EP-145
// D11: "locale in i18n server, zod provide the keys"). Each side localizes its
// own errors and neither decodes the other's language: `fieldMessage` renders a
// client key through i18n and prints a server detail verbatim, because the
// server already wrote it in the applicant's language
// (Api::RegistrationsController#validation_details).
//
// DRIFT RISK, recorded deliberately: the same field rules now carry copy in two
// places, the server's errors.{en,th}.yml and this file's auth.ts namespace.
// Each side has its own parity spec covering itself and nothing compares the
// two, so a rule changed on one side can be phrased differently on the other
// without any test noticing.
const VALIDATION_NS = 'signup.validation.'

// The caps ride with the key rather than being baked into a message at schema
// build time, which is what lets the schema be a module constant instead of a
// value rebuilt on every language change.
const VALIDATION_PARAMS: Record<string, { max: number }> = {
  [`${VALIDATION_NS}account_name_too_long`]: { max: ACCOUNT_NAME_MAX },
  [`${VALIDATION_NS}first_name_too_long`]: { max: PERSON_NAME_MAX },
  [`${VALIDATION_NS}last_name_too_long`]: { max: PERSON_NAME_MAX },
  [`${VALIDATION_NS}address_too_long`]: { max: ADDRESS_MAX },
}

const signupSchema = z.object({
  account_name: z
    .string()
    .trim()
    .min(1, `${VALIDATION_NS}account_name_required`)
    .max(ACCOUNT_NAME_MAX, `${VALIDATION_NS}account_name_too_long`),
  owner_first_name: z
    .string()
    .trim()
    .min(1, `${VALIDATION_NS}first_name_required`)
    .max(PERSON_NAME_MAX, `${VALIDATION_NS}first_name_too_long`),
  owner_last_name: z
    .string()
    .trim()
    .min(1, `${VALIDATION_NS}last_name_required`)
    .max(PERSON_NAME_MAX, `${VALIDATION_NS}last_name_too_long`),
  email: z.string().trim().email(`${VALIDATION_NS}email_invalid`),
  phone: z
    .string()
    .trim()
    .refine(
      (value) =>
        PHONE_ALLOWED_CHARS.test(value) &&
        digitCount(value) >= PHONE_MIN_DIGITS &&
        digitCount(value) <= PHONE_MAX_DIGITS,
      `${VALIDATION_NS}phone_invalid`,
    ),
  address: z
    .string()
    .trim()
    .min(1, `${VALIDATION_NS}address_required`)
    .max(ADDRESS_MAX, `${VALIDATION_NS}address_too_long`),
  invitation_code: z.string().trim().length(INVITATION_CODE_LENGTH, `${VALIDATION_NS}code_length`),
  // EP-160 (section 2.7, AC-10): contract acceptance at sign-up. The server
  // refuses anything but true (details.terms_accepted).
  terms_accepted: z.boolean().refine((value) => value, `${VALIDATION_NS}terms_required`),
  // EP-160 S9 (D4): optional, ticked by default.
  support_onboarding_grant: z.boolean(),
})

type SignupFormValues = z.infer<typeof signupSchema>

export function SignupPage() {
  const { t, i18n } = useTranslation('auth')
  const isEnglish = i18n.language === 'en'

  const [formError, setFormError] = useState('')
  const [registeredEmail, setRegisteredEmail] = useState('')
  const successHeadingRef = useRef<HTMLHeadingElement | null>(null)

  const signup = useSignup()
  const [searchParams] = useSearchParams()

  const {
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      account_name: '',
      owner_first_name: '',
      owner_last_name: '',
      email: '',
      phone: '',
      address: '',
      invitation_code: invitationCodeFromQuery(searchParams.get('code')),
      terms_accepted: false,
      support_onboarding_grant: true,
    },
  })

  const inFlight = isSubmitting || signup.isPending

  // A client message is one of this page's own i18n keys; a server message is
  // already-localized prose. `type` is the discriminator react-hook-form gives
  // for free: setError below always stamps 'server', while the Zod resolver
  // stamps the issue code.
  const fieldMessage = (error?: FieldError) => {
    if (!error?.message) return undefined
    if (error.type === 'server') return error.message
    return t(error.message, VALIDATION_PARAMS[error.message])
  }

  // The form UNMOUNTS on success and the panel below takes its place, so
  // without moving focus a screen-reader user hears nothing at all after
  // pressing submit: the caret is left on a button that no longer exists. The
  // heading is the panel's own first line, so focus lands where a sighted user
  // is already looking. role="status" additionally announces the panel for a
  // user who has moved focus elsewhere while the request was in flight.
  useEffect(() => {
    if (registeredEmail) successHeadingRef.current?.focus()
  }, [registeredEmail])

  const applyServerError = (error: unknown) => {
    const response = (error as { response?: { status?: number; data?: SignupErrorBody } }).response
    const code = response?.data?.error?.code
    const details = response?.data?.error?.details

    if (response?.status === 429 || code === 'rate_limited') {
      setFormError(t('signup.errors.rate_limited'))
      return
    }

    if (code === 'invitation_code_invalid') {
      const message = t('signup.errors.invitation_code_invalid')
      setFormError(message)
      setError('invitation_code', { type: 'server', message }, { shouldFocus: true })
      return
    }

    if (code === 'email_taken') {
      const message = t('signup.errors.email_taken')
      setFormError(message)
      setError('email', { type: 'server', message }, { shouldFocus: true })
      return
    }

    if (code === 'validation_failed') {
      setFormError(t('signup.errors.validation_failed'))
      let focused = false
      Object.entries(details ?? {}).forEach(([field, messages]) => {
        if (!isSignupField(field)) return
        setError(
          field,
          { type: 'server', message: messages.join(', ') },
          // Focus the first invalid field only, so the page does not fight
          // itself over which input the caret lands in.
          { shouldFocus: !focused },
        )
        focused = true
      })
      return
    }

    setFormError(t('signup.errors.generic'))
  }

  const onSubmit = handleSubmit(async (values) => {
    setFormError('')
    try {
      const result = await signup.mutateAsync({
        ...values,
        // BA-31: never a form field. The applicant has no account yet, so the
        // only language signal is the one the form was filled in.
        language: isEnglish ? 'en' : 'th',
      })
      setRegisteredEmail(result.data.email)
    } catch (error) {
      applyServerError(error)
    }
  })

  return (
    <div className="min-h-screen bg-primary flex flex-col lg:grid lg:grid-cols-[2fr_1fr]">
      {/* Mobile collage backdrop, the LoginPage recipe verbatim (iOS Safari
          paints the status-bar strip from a FIXED positive-z element only),
          shortened because the sign-up form column is much taller. */}
      <div
        aria-hidden="true"
        className="lg:hidden fixed inset-x-0 top-0 z-50 h-[32vh] overflow-hidden bg-primary pointer-events-none"
      >
        <div className="absolute inset-[-30%] -translate-y-28 rotate-[-10deg] scale-125 bg-[url('/images/login-collage.png')] bg-repeat opacity-60 [background-size:440px_auto]" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/55 to-primary/80" />
      </div>

      <LoginBrandPanel className="relative z-[60] flex flex-1 min-h-[16vh] pt-[max(env(safe-area-inset-top),1rem)]" />

      <div className="relative z-[60] -mt-12 lg:mt-0 bg-base-100 rounded-t-3xl lg:rounded-none flex flex-col justify-start lg:justify-center px-6 pt-8 pb-[max(env(safe-area-inset-bottom),2rem)] sm:px-10 lg:px-16">
        <div className="w-full max-w-sm mx-auto">
          <div className="flex items-center justify-start lg:justify-center gap-2 mb-6">
            <img src="/icon-clean.svg" alt="" className="w-9 h-9 rounded-lg ring-1 ring-base-300" />
            <span className="font-bold text-lg text-base-content">FlowRent</span>
          </div>

          {registeredEmail ? (
            <div className="space-y-4" role="status">
              <div>
                <h1
                  className="text-2xl font-bold focus:outline-none"
                  tabIndex={-1}
                  ref={successHeadingRef}
                >
                  {t('signup.success.title')}
                </h1>
                <p className="text-base-content/60">
                  {t('signup.success.body', { email: registeredEmail })}
                </p>
              </div>

              <Alert variant="info">
                <div className="space-y-1">
                  <p>{t('signup.success.expiry', { hours: LINK_VALID_HOURS })}</p>
                  <p>{t('signup.success.retry')}</p>
                </div>
              </Alert>

              <Button as={Link} to="/login" block variant="outline">
                {t('signup.success.sign_in')}
              </Button>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h1 className="text-2xl font-bold">{t('signup.title')}</h1>
                <p className="text-base-content/60">{t('signup.subtitle')}</p>
              </div>

              <form onSubmit={onSubmit} noValidate>
                <div className="space-y-4">
                  {formError && (
                    <Alert variant="error" icon={null} role="alert">
                      <span>{formError}</span>
                    </Alert>
                  )}

                  <Input
                    id="signup-account-name"
                    label={t('signup.account_name')}
                    error={fieldMessage(errors.account_name)}
                    {...register('account_name')}
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      id="signup-first-name"
                      className="min-w-0 w-full"
                      label={t('signup.owner_first_name')}
                      autoComplete="given-name"
                      error={fieldMessage(errors.owner_first_name)}
                      {...register('owner_first_name')}
                    />
                    <Input
                      id="signup-last-name"
                      className="min-w-0 w-full"
                      label={t('signup.owner_last_name')}
                      autoComplete="family-name"
                      error={fieldMessage(errors.owner_last_name)}
                      {...register('owner_last_name')}
                    />
                  </div>

                  <Input
                    id="signup-email"
                    label={t('signup.email')}
                    type="email"
                    autoComplete="email"
                    placeholder={t('signup.email_placeholder')}
                    error={fieldMessage(errors.email)}
                    {...register('email')}
                  />

                  <Input
                    id="signup-phone"
                    label={t('signup.phone')}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder={t('signup.phone_placeholder')}
                    error={fieldMessage(errors.phone)}
                    {...register('phone')}
                  />

                  <Textarea
                    id="signup-address"
                    label={t('signup.address')}
                    rows={2}
                    autoComplete="street-address"
                    error={fieldMessage(errors.address)}
                    {...register('address')}
                  />

                  <Input
                    id="signup-invitation-code"
                    label={t('signup.invitation_code')}
                    // A real text keyboard, never type="number": the codes are
                    // alphanumeric and a numeric keypad cannot type them.
                    inputClassName="font-mono uppercase tracking-[0.3em]"
                    maxLength={INVITATION_CODE_LENGTH}
                    autoCapitalize="characters"
                    autoComplete="off"
                    hint={t('signup.invitation_code_hint')}
                    error={fieldMessage(errors.invitation_code)}
                    {...register('invitation_code', {
                      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
                        setValue('invitation_code', event.target.value.toUpperCase())
                      },
                    })}
                  />

                  <a
                    href={isEnglish ? CONTACT_URL_EN : CONTACT_URL_TH}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link link-hover text-sm text-base-content/60 inline-block"
                  >
                    {t('signup.no_code')}
                  </a>

                  {/* EP-160 (section 2.7, AC-10): the sentence stays whole in
                      the label; the two documents are linked below it rather
                      than cut into the sentence, so each language keeps its
                      own word order. */}
                  <div>
                    <Checkbox
                      id="signup-terms-accepted"
                      variant="primary"
                      label={t('signup.terms.accept')}
                      aria-invalid={errors.terms_accepted ? true : undefined}
                      aria-describedby={errors.terms_accepted ? 'signup-terms-accepted-error' : undefined}
                      {...register('terms_accepted')}
                    />
                    <div className="mt-1 ml-8 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                      <a
                        href={TERMS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link link-hover text-primary"
                      >
                        {t('signup.terms.terms_link')}
                      </a>
                      <a
                        href={PRIVACY_POLICY_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="link link-hover text-primary"
                      >
                        {t('signup.terms.privacy_link')}
                      </a>
                    </div>
                    {errors.terms_accepted && (
                      <p id="signup-terms-accepted-error" className="mt-1 text-sm text-error">
                        {fieldMessage(errors.terms_accepted)}
                      </p>
                    )}
                  </div>

                  {/* EP-160 S9 (D4): the onboarding grant. A customer who
                      stops signing in cannot grant anything later, so it is
                      offered here and ticked by default. */}
                  <div>
                    <Checkbox
                      id="signup-support-onboarding-grant"
                      variant="primary"
                      label={t('signup.support_onboarding.label')}
                      aria-describedby="signup-support-onboarding-grant-detail"
                      {...register('support_onboarding_grant')}
                    />
                    <p id="signup-support-onboarding-grant-detail" className="mt-1 ml-8 text-sm text-base-content/60">
                      {t('signup.support_onboarding.detail')}
                    </p>
                  </div>
                </div>

                <Button
                  type="submit"
                  data-testid="signup-submit"
                  block
                  className="mt-6"
                  loading={inFlight}
                  disabled={inFlight}
                >
                  {t('signup.submit')}
                </Button>
              </form>

              <div className="mt-4 text-center">
                <Link to="/login" className="link link-hover text-sm text-primary">
                  {t('signup.have_account')}
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
